import { useReactiveVar } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import {
  AuthenticationState,
  Path,
  RedirectState,
  env,
  getForwardedAuthParams,
  getPath,
  useAuthContinueUrl,
  useCurrentUser,
  useUrlQueryParam,
  withForwardedAuthParams,
} from '@neo.tax/shared';
import { useUserFromAuth } from '@neo.tax/shared/hooks/useUserFromAuth';

export const useAuthRedirect = (): RedirectState => {
  const { authenticationServiceState, isAuthenticating } = useReactiveVar(AuthenticationState.authenticationStateVar);
  const authenticationUser = authenticationServiceState.authenticationUser;
  const { loading, user } = useCurrentUser();
  const userId = useUrlQueryParam('userId');
  const isImpersonating = !!userId;

  // OAuth/SAML/OIDC flows return to the original signup URL (`/signup?inviteToken=…`)
  // unchanged after `signInWithPopup` resolves — the popup never rewrites the parent
  // window's location. useAuthContinueUrl derives a continueUrl from inviteToken so
  // post-OAuth redirect also lands on /accept-invitation, matching the email/password
  // path that explicitly sets `?continueUrl=`.
  const safeContinueUrl = useAuthContinueUrl();

  // Distinguishes an unverified session that IS provisioned (→ verify-email) from one that is
  // authenticated in Firebase but has no DB user yet (→ finish signup). getUserFromAuth is
  // @SkipAuth, so this resolves even for an unverified session.
  const { existsInDb, loading: userFromAuthLoading } = useUserFromAuth();
  const location = useLocation();

  // An unverified Firebase session can't complete reconciliation — every
  // authenticated API call is rejected by the backend guard with
  // EMAIL_NOT_VERIFIED. Redirect to the verify flow so the session can be
  // resolved. Checked AFTER the hooks above to satisfy rules-of-hooks.
  //
  // `isAuthenticating` gates this during OAuth: SAML/OIDC's first
  // `onIdTokenChanged` after `signInWithPopup` carries `emailVerified: false`
  // (SAML IdPs don't propagate the claim). `startOAuth` flips it server-side
  // and force-refreshes the token immediately after; without this guard React
  // can render on the stale value and redirect before the refresh lands.
  if (authenticationUser && !authenticationUser.emailVerified && !isAuthenticating) {
    // Invited users are the one unverified case we don't bounce to verification: accepting the
    // invite proves they control the invited inbox (the one-time token was emailed there) and
    // the backend marks their email verified as part of acceptance, so send them straight to
    // /accept-invitation. The mutation carries @SkipAuthEmailCheck to accept the unverified
    // session. We can't just fall through to the `if (user)` continueUrl forward below — GET_USER
    // is itself email-verified-gated, so for an unverified session `user` never loads.
    if (safeContinueUrl?.startsWith(getPath({ path: Path.ACCEPT_INVITATION }))) {
      return { isLoading: false, redirectTo: safeContinueUrl };
    }

    // Resolve DB-user existence before deciding orphan-vs-verify.
    if (userFromAuthLoading) {
      return { isLoading: true };
    }

    // Authenticated in Firebase but no DB user yet → send to /signup to finish provisioning, rather
    // than the verify-email page (whose resend can't work without a DB user). Avoid a redirect loop:
    // Signup itself runs this hook, so once we're on /signup we render it (it handles the
    // unprovisioned session) instead of redirecting again.
    if (!existsInDb) {
      const signupPath = getPath({ path: Path.SIGNUP });
      return location.pathname === signupPath ? { isLoading: false } : { isLoading: false, redirectTo: signupPath };
    }

    // Provisioned but unverified → verify flow. Forward any pending `continueUrl` so the user still
    // lands where they were headed (e.g. /accept-invitation) after verifying.
    const verifySentPath = getPath({ path: Path.VERIFY_EMAIL_SENT });
    const query = safeContinueUrl
      ? `?reason=security-update&continueUrl=${encodeURIComponent(safeContinueUrl)}`
      : '?reason=security-update';
    return { isLoading: false, redirectTo: `${verifySentPath}${query}` };
  }

  if (loading || authenticationServiceState.loading) {
    return { isLoading: true };
  }

  if (user) {
    // `?continueUrl=` beats the default post-login destinations so a login initiated from
    // an invite flow returns the user to /accept-invitation?token=… instead of the dashboard.
    if (safeContinueUrl) {
      return { isLoading: false, redirectTo: safeContinueUrl };
    }
    // Forward `?continueUrl=` onto /profile when bouncing an incomplete user there, so
    // useProfileRedirect can continue them to the intended destination once onboarding
    // completes — avoids stranding a user who was headed somewhere specific (e.g. an
    // accept-invitation URL) on the profile form.
    const profilePath = getPath({ path: Path.PROFILE });
    const profilePathWithContinue = safeContinueUrl
      ? `${profilePath}?continueUrl=${encodeURIComponent(safeContinueUrl)}`
      : profilePath;
    if (user.userAttributes.isSsoOnboarding) {
      return {
        isLoading: false,
        redirectTo: profilePathWithContinue,
      };
    }
    if (!user.organization && user.companies.length === 0) {
      return {
        isLoading: false,
        redirectTo: profilePathWithContinue,
      };
    }
    return {
      isLoading: false,
      redirectTo: getPath({ path: Path.COMPANIES }),
    };
  }

  if (env.REACT_APP_ENTERPRISE_SSO_LOGIN_ONLY === true && !isImpersonating) {
    // Preserve invite/continue params so SAML/OIDC sign-in still routes to /accept-invitation.
    return {
      isLoading: false,
      redirectTo: withForwardedAuthParams(getPath({ path: Path.ENTERPRISE_SSO }), getForwardedAuthParams()),
    };
  }
  return {
    isLoading: false,
  };
};
